import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Search } from "lucide-react";
import { api, errMsg, downloadFile } from "../lib/api";
import { inr, STATUSES } from "../lib/format";
import { POCard } from "../components/po/POCard";
import { OverdueAlert } from "../components/po/OverdueAlert";
import { ReviewQueue } from "../components/po/ReviewQueue";

export default function Register() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get("/pos", { params: { q, status } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    window.addEventListener("review:new", load);
    return () => window.removeEventListener("review:new", load);
  }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      await downloadFile("/export/excel", "PO-Register.xlsx");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const s = data?.summary;
  const openPo = (pid) => {
    setQ("");
    setStatus("All");
    setOpen(pid);
    setTimeout(() => document.querySelector(`[data-po-id="${pid}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
  };
  return (
    <div className="space-y-4" data-testid="register-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="mono-label text-brand">All purchase orders</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">PO Register</h1>
        </div>
        <button className="btn btn-outline shrink-0 px-3" onClick={exportExcel} disabled={exporting} data-testid="download-excel-btn">
          {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />} Excel
        </button>
      </div>

      <ReviewQueue review={data?.review} onOpen={openPo} onChanged={load} />
      <OverdueAlert overdue={data?.overdue} onOpen={openPo} />

      {s && (
        <p className="panel flex flex-wrap gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="register-summary">
          <span><b className="mono">{s.draft}</b> draft</span>
          <span><b className="mono">{s.accepted}</b> accepted</span>
          <span>Accepted value <b className="mono">{inr(s.accepted_value)}</b></span>
        </p>
      )}

      <div className="relative">
        <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input className="field !pl-10" placeholder="Search PO number, vendor or item" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="register-search-input" />
      </div>
      <div className="seg">
        {["All", ...STATUSES].map((x) => (
          <button key={x} data-active={status === x} onClick={() => setStatus(x)} data-testid={`filter-${x.toLowerCase()}`}>{x}</button>
        ))}
      </div>

      {!data ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : data.items.length === 0 ? (
        <p className="panel p-6 text-center text-muted-foreground" data-testid="register-empty">No purchase orders found.</p>
      ) : (
        <div className="space-y-3" data-testid="po-list">
          {data.items.map((po) => (
            <POCard key={po.id} po={po} settings={settings} open={open === po.id}
              onToggle={() => setOpen(open === po.id ? null : po.id)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
